import React, { createContext, type ReactNode, useContext, useMemo } from "react"
import type {
  ProviderAttachmentKind,
  ProviderOutgoingAttachment,
  ProviderThreadList,
  ProviderThreadMessages
} from "../providerMessagingTypes"

/**
 * The PROVIDER (Healthie) messaging transport, reached through the host.
 *
 * ERROR SHAPE: every method's rejections must carry a numeric `statusCode` (`ProviderApiError`),
 * which is what the retry switches in `hooks/useProviderThreads.ts` and `hooks/useThreadMessages.ts`
 * read. Mapping the transport's own errors into that shape is the HOST adapter's job. That is what
 * keeps the package transport-agnostic: nothing here imports the BFF client or knows that tRPC
 * exists.
 */
export type MessagesProviderApi = {
  /**
   * The member's provider conversations — ALL of them, open and closed. NO PARAMETERS: the transport
   * answers the whole list, and there is no "load more" control anywhere in this feature for a page
   * boundary to serve.
   *
   * Answers with `{ threads, hasProviderIdentity }` rather than the array itself. The flag is a
   * property of the MEMBER, not of the list, so an empty `threads` alone cannot express it: see
   * `ProviderThreadList`. A host whose transport genuinely cannot tell should report `true` — the
   * conservative answer, since it claims nothing about what the member does or does not have.
   */
  getThreads: () => Promise<ProviderThreadList>
  /** One thread's whole history, oldest-first. Unpaged, for the same reason `getThreads` is. */
  getThreadMessages: (params: { threadId: string }) => Promise<ProviderThreadMessages>
  /**
   * `clientMessageId` is the caller's idempotency key for the send.
   *
   * `body` is REQUIRED even for a file with no text, which then carries `""` — the transport's
   * blankness rule is "text, a file, or both", so making it optional would be a type change where
   * only a validation changed.
   */
  sendMessage: (params: {
    threadId: string
    body: string
    clientMessageId: string
    /**
     * ONE file to send with the message (CONAPP-367), absent on the ordinary send.
     *
     * ⚠ NOTHING IN THIS PACKAGE DECIDES WHETHER A FILE MAY BE SENT. The type allowlist, the size cap
     * and the bytes-match-the-declared-type check are all the transport's, server-side; a composer
     * that checks the same things is telling the member early, not deciding. A host whose transport
     * has the feature turned off REJECTS this field rather than dropping it, so an attachment that
     * cannot be sent surfaces as a `statusCode`-bearing rejection the composer can branch on —
     * never as a message that silently loses its file.
     */
    attachment?: ProviderOutgoingAttachment
  }) => Promise<{ messageId: string; createdAt: number }>
  markRead: (params: { threadId: string }) => Promise<void>
  /**
   * Where a member's click on one of a message's attachments goes.
   *
   * ⚠ A URL, NOT A CALL, and the only member of this contract that is not a promise. That is the
   * point of it: Healthie signs every attachment url with a ten-second expiry, so the file cannot be
   * fetched ahead of the click and handed over — the href has to be a STABLE url that mints one when
   * followed, and following it is a navigation the browser performs, not a request this package
   * makes. (Fetching it here would also mean holding a bearer credential for a patient's file in
   * front-end memory.)
   *
   * It is on the HOST contract rather than built in this package for the same reason the transport
   * is: the BFF's path is the host's to know. The consumer app answers `/bff/messages/attachment/…`.
   */
  attachmentUrl: (params: { messageId: string; kind: ProviderAttachmentKind }) => string
}

// The HOST contract handed into the provider messages panel.
//
// It exists because everything this feature needs from the app is unreachable from a workspace
// package: the BFF client is host-only, and so is Sentry. The `#/*` alias is a root-tsconfig path, so
// no `packages/*` file can import any of it.
//
// The split is deliberate: the host supplies TRANSPORT, error reporting and its own runtime config,
// and the package owns the react-query layer. Cache keys, `staleTime` and the retry switches live in
// the package, once each.
//
// FOUR KEYS, where this used to carry the whole Carrot messaging surface as well — the second half
// of a page that no longer lives here. Provider messaging is now the package's only subject: the
// Carrot side of /messages is the app's own (src/components/messaging/**), framed by the app's own
// shell (src/pages/messages-shell.tsx), and the tab bar there is what puts the two beside each other.
// Nothing in this package renders host chrome any more, so `components` is gone with it.
//
// Mirrors the pattern in packages/consumer/src/subscription/context/SubscriptionHostContext.tsx.
export type MessagesHostContract = {
  // REQUIRED, unlike its predecessor. It used to be optional because the package served a host that
  // might have no provider transport at all; the panel it serves now IS the provider transport's UI,
  // so a host without one has nothing to mount. `useProviderUnreadCount` still takes its `getThreads`
  // as an ordinary parameter that may be absent — that is the nav dot deciding not to ask, which is a
  // different thing from a host that cannot answer.
  providerApi: MessagesProviderApi
  // The host's Sentry wrappers (src/utils/ErrorReporting.ts).
  reportError: (error: unknown, context?: Record<string, string>) => void
  reportErrorMessage: (message: string) => void
  /**
   * Whether this deployment has message attachments turned on (CONAPP-367) — the CLIENT half of the
   * gate, and the composer's only input to whether it renders an attach control at all.
   *
   * ⛔ OPTIONAL, AND ABSENT IS OFF. It fails closed on purpose: a host that has never heard of the
   * feature must not acquire it by upgrading the package.
   *
   * ⚠ NOT A SECOND SOURCE OF TRUTH, and hiding the button decides nothing on its own. The BFF
   * refuses a send carrying a file unless its OWN `HEALTHIE_MESSAGE_ATTACHMENTS_ENABLED` is "true"
   * (routers/messages.ts), which is what makes this a gate rather than a decoration — the procedure
   * is reachable without the composer. Both halves exist deliberately; a deployment that turns one
   * on and not the other gets a member who can pick a file and a transport that refuses it, which
   * the composer surfaces as a refusal rather than as a lost file.
   *
   * A BOOLEAN, resolved by the HOST from its own runtime config (`window.env` via
   * `CarrotConfig.ts`). Nothing in this package reads host config directly — the same rule the
   * consumer package's `stripePublishableKey` follows, and here it is also a hard constraint:
   * `window.env`'s type augmentation is a host-side declaration no `packages/*` file can see.
   */
  attachmentsEnabled?: boolean
}

const MessagesHostContext = createContext<MessagesHostContract | null>(null)

export function useMessagesHost(): MessagesHostContract {
  const ctx = useContext(MessagesHostContext)
  if (ctx === null) {
    throw new Error("useMessagesHost must be used within <MessagesHostProvider>")
  }
  return ctx
}

export function MessagesHostProvider({
  providerApi,
  reportError,
  reportErrorMessage,
  attachmentsEnabled,
  children
}: MessagesHostContract & { children: ReactNode }): JSX.Element {
  const value = useMemo<MessagesHostContract>(
    () => ({ providerApi, reportError, reportErrorMessage, attachmentsEnabled }),
    [providerApi, reportError, reportErrorMessage, attachmentsEnabled]
  )
  return <MessagesHostContext.Provider value={value}>{children}</MessagesHostContext.Provider>
}
